import { Component, OnInit, Input, ChangeDetectorRef } from '@angular/core';

import { AuthUser, OrgInfo } from '@app/account/account-api.model';
import {
  ProductHeaderService,
  ProductName,
} from '@app/layout/product-header.service';
import {
  GlobalNavigationService,
  NavigationInfo,
} from '@app/shared/services/global-navigation.service';
import { TeamFlagsService } from '@app/team/services/team-flags.service';
import { ThumbnailService } from '@app/thumbnails/thumbnail.service';
import {
  AuthService,
  ColorService,
  LDFlagsService,
  TargetsService,
} from '@dg/shared-services';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, tap } from 'rxjs';

@Component({
  selector: 'dgx-lxp-nav',
  templateUrl: './lxp-nav.component.html',
  styleUrls: ['./lxp-nav.component.scss'],
})
export class LxpNavComponent implements OnInit {
  @Input() public productImagePath?: string;
  @Input() public orgId?: number;
  @Input() public context: ProductName = ProductName.LearnerHome;

  public navExpanded: boolean = false;
  public navigationInfo: NavigationInfo;
  private navigationInfoSubscription: Subscription;

  public isSkillCoachEnabled: boolean = false;
  public isOpportunitiesEnabled: boolean = false;
  public isDegreedLearnerHome: boolean;
  public featuredPlanId: number = null;
  public hasLightText: boolean = false;
  public brandName: string;
  public brandColor: string;
  public showBrandLogo: boolean;
  public brandLogoSrcset: {
    retina: string;
    orig: string;
  };
  public productImageSrcset: {
    retina: string;
    orig: string;
  };
  public productImageSrcValue: string;
  public orgInfo?: OrgInfo;

  public i18n = this.translate.instant([
    'A11y_MainPagesNavType',
    'Core_Home',
    'Core_Search',
    'Core_AddContent',
    'AppHeader_HomeTitle',
    'AppHeader_BrowseTitle',
    'AppHeader_OpportunitiesTitle',
    'AppHeader_ProfileTitle',
    'AppHeader_SkillCoachTitle',
    'AppHeader_FeaturedTitle',
    'AppHeader_AcademiesTitle',
    'AppHeader_FeaturedTitle',
    'AppHeader_ProfileTitle',
    'User_Notifications',
  ]);

  private currentOrgId: number = null;

  // TODO: replace this hack when Profile has router navigation
  public get isOnProfile(): boolean {
    return document.location.pathname.split('/')[1] === 'profile';
  }

  public get showFeaturedNavLink(): boolean {
    return (
      this.featuredPlanId &&
      !this.authUser?.isSkillAnalyticsClient &&
      !this.authUser?.isSkillInventoryClient
    );
  }

  public get showOpportunitiesNavLink(): boolean {
    return this.authService.authUser?.hasCareerMobility;
  }

  public get showGlobalAdd(): boolean {
    // TODO: also shouldn't allow for 'isClientProvider' (check utility-bar.component.html)
    return (
      this.isUserAuthenticated &&
      !this.authUser?.isSkillAnalyticsClient &&
      !this.authUser?.isSkillInventoryClient
    );
  }

  public get isAcademiesEnabled(): boolean {
    return !!this.authUser && this.ldflagService.showAcademies;
  }

  public get isDegreedAssistantEnabled(): boolean {
    return this.ldflagService.lxpDegreedAssistant;
  }

  public get isUserAuthenticated(): boolean {
    return !!this.authUser;
  }

  public get profileUrlName(): string {
    return this.authUser.viewerProfile.profileUrl;
  }

  public get authUser(): AuthUser {
    return this.authService.authUser;
  }

  // cheats for now
  public get isHome(): boolean {
    return window.location.href.includes('/dashboard/');
  }
  public get isFeatured(): boolean {
    return window.location.href.includes('/explore/');
  }
  public get isProfile(): boolean {
    return window.location.href.includes('/profile/');
  }
  public get isOpportunities(): boolean {
    return window.location.href.includes('/career/opportunities');
  }
  public get isSearch(): boolean {
    return window.location.href.includes('/learning/');
  }
  public get isNotifications(): boolean {
    return window.location.href.includes('/notifications');
  }

  constructor(
    private targetsService: TargetsService,
    private authService: AuthService,
    private translate: TranslateService,
    private thumbnailService: ThumbnailService,
    private productHeaderService: ProductHeaderService,
    private colorService: ColorService,
    private teamFlagsService: TeamFlagsService,
    private globalNavigationService: GlobalNavigationService,
    private cdr: ChangeDetectorRef,
    private ldflagService: LDFlagsService
  ) {}

  ngOnInit(): void {
    this.navigationInfoSubscription = this.setupNavigationSubscription();

    // TODO: fix padding top (temp until we have the css layout stuff migrated)
    (
      document.getElementsByClassName('l_max-screen')[0] as HTMLElement
    ).style.paddingTop = '6.5rem';

    if (this.authUser) {
      this.orgInfo = this.orgId
        ? this.authUser.orgInfo.find((x) => x.organizationId === this.orgId) // for multi org users
        : this.authUser.defaultOrgInfo; // this could be null for consumer users

      if (this.orgInfo) {
        this.currentOrgId = this.orgId || this.orgInfo.organizationId;
        this.targetsService
          .getBrowseTarget(this.currentOrgId)
          .pipe(tap((target) => (this.featuredPlanId = target?.targetId)))
          .subscribe();

        this.initCoBranding();
        this.productImageSrcset = this.getProductImageThumbnail();

        // hide or show the opportunities nav
        this.isOpportunitiesEnabled = this.authUser?.hasCareerMobility;

        // hide or show skill coach
        if (
          this.authUser.isSkillInventoryClient ||
          this.authUser.isSkillAnalyticsClient
        ) {
          this.isSkillCoachEnabled = this.authUser.isManager;
        } else {
          this.isSkillCoachEnabled =
            this.authUser.isManager &&
            this.authUser.defaultOrgInfo.settings.enableTeamSpace &&
            (this.authUser.defaultOrgInfo.settings.skillCoachFullOrgAccess ||
              this.teamFlagsService.teamSpaceEnabled);
        }
      }
    }
  }

  /**
   * Lifecycle hook called just before Angular destroys the directive or component.
   */
  ngOnDestroy(): void {
    if (this.navigationInfoSubscription) {
      this.navigationInfoSubscription.unsubscribe();
    }
  }

  private initCoBranding() {
    const defaultBrandColor = this.colorService.getColor('white');
    const defaultHasLightText = false;

    this.brandColor = this.orgInfo?.organizationBranding?.brandColor
      ? this.orgInfo?.organizationBranding?.brandColor
      : defaultBrandColor;
    this.hasLightText = this.orgInfo?.organizationBranding?.useLightText
      ? this.orgInfo?.organizationBranding?.useLightText
      : defaultHasLightText;
    this.brandLogoSrcset = this.orgInfo?.image
      ? this.thumbnailService.fetchProxyImageSrcset({
          imageSrc: this.orgInfo.image,
          imageHeight: 36,
          crop: 'fit',
        })
      : null;
    this.brandName = this.orgInfo?.name;
    this.showBrandLogo = !!this.brandLogoSrcset;
    this.isDegreedLearnerHome =
      this.orgInfo?.organizationId == 1 && this.context === 'learner-home';
  }

  private getProductImageThumbnail() {
    return this.productImagePath
      ? this.thumbnailService.fetchProxyImageSrcset({
          imageSrc: this.productImagePath,
          imageHeight: 36,
          crop: 'fit',
        })
      : this.productHeaderService.getProductImageSrcset(
          this.context,
          this.hasLightText,
          this.orgInfo?.settings?.isClientProvider
        );
  }

  /**
   * Sets up the subscription to the NavigationService's observable.
   * @returns Subscription to the NavigationService's observable.
   */
  private setupNavigationSubscription(): Subscription {
    return this.globalNavigationService.navigationInfo$.subscribe(
      (navigationInfo) => {
        this.navigationInfo = navigationInfo;
        this.cdr.detectChanges();
      }
    );
  }
}
